import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Grid, Box, Alert, Card, CardContent, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { toast } from '../../components/toast';
import { getUserGroups } from '../../utils';
import { useEmployeeDashboardOperations } from '../../hooks/useEmployeeDashboardOperations';
import DateRangeFilter from '../../components/DateRangeFilter';
import { fetchEmployeePayrollMonthlyDummy } from '../../utils/employeeDashboardDummyApi';
import {
  EmployeeDashboardStatsCards,
  EmployeeDashboardFilters,
  EmployeeDashboardCharts,
  EmployeeDashboardAttendanceCalendar,
  EmployeeDashboardPayrollMonthlyChart,
} from '../../components/employee-dashboard';
import { HRPageHeader } from '../../components/hr';

export default function EmployeeDashboardHR() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const employeeFilter = searchParams.get('employee_id') || '';

  const [dateRange, setDateRange] = useState(() => {
    const today = dayjs();
    return {
      startDate: today.startOf('month').format('YYYY-MM-DD'),
      endDate: today.endOf('month').format('YYYY-MM-DD'),
      preset: 'thisMonth',
    };
  });

  const [payrollMonthlyPayments, setPayrollMonthlyPayments] = useState([]);

  const {
    dashboardLoading,
    attendanceStats,
    leaveStats,
    payrollStats,
    calendarDays,
    filterOptions,
    loadFilterOptions,
    loadDashboardData,
    resetDashboardData,
  } = useEmployeeDashboardOperations();

  const hasSelectedEmployee = Boolean(employeeFilter);

  useEffect(() => {
    const userGroups = getUserGroups();
    if (userGroups === null || (!userGroups.includes('admin') && !userGroups.includes('hr'))) {
      toast.error('Access denied. HR or Admin privileges required.');
      navigate('/');
    } else {
      loadFilterOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!employeeFilter) {
      resetDashboardData();
      setPayrollMonthlyPayments([]);
    } else {
      loadDashboardData(employeeFilter, 'All', 'All', 'All', dateRange.startDate, dateRange.endDate);

      fetchEmployeePayrollMonthlyDummy(employeeFilter, dateRange.startDate, dateRange.endDate)
        .then((pay) => {
          if (!cancelled) {
            setPayrollMonthlyPayments(pay.monthlyPayments);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setPayrollMonthlyPayments([]);
          }
        });
    }

    return () => {
      cancelled = true;
    };
  }, [employeeFilter, dateRange.startDate, dateRange.endDate, loadDashboardData, resetDashboardData]);

  const handleFilterChange = (filterType, value) => {
    if (filterType !== 'employee') {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    if (value) {
      nextSearchParams.set('employee_id', value.toString());
    } else {
      nextSearchParams.delete('employee_id');
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  const handleDateRangeChange = ({ startDate, endDate, preset }) => {
    setDateRange({
      startDate: startDate || null,
      endDate: endDate || null,
      preset: preset || 'custom',
    });
  };

  const sectionLoading = dashboardLoading;

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | Employee Dashboard</title>
      </Helmet>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <HRPageHeader
            title="Employee dashboard"
            description="Monitor headcount, attendance trends, and payroll totals for the selected period. Adjust the date range to compare different time frames."
          />
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent
              sx={{
                py: 2,
                '&:last-child': { pb: 2 },
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', md: 'flex-start' }}
                useFlexGap
              >
                <EmployeeDashboardFilters
                  disableCard
                  employeeFilter={employeeFilter}
                  filterOptions={filterOptions}
                  employeesList={filterOptions.employees}
                  onFilterChange={handleFilterChange}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <DateRangeFilter
                    label="Dashboard period"
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    selectedPreset={dateRange.preset}
                    onDateRangeChange={handleDateRangeChange}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {!hasSelectedEmployee && (
          <Grid item xs={12}>
            <Alert severity="info">Select an employee to load dashboard data.</Alert>
          </Grid>
        )}

        {hasSelectedEmployee && (
          <>
            <Grid item xs={12}>
              <EmployeeDashboardStatsCards
                attendanceStats={attendanceStats}
                leaveStats={leaveStats}
                payrollStats={payrollStats}
                isLoading={dashboardLoading}
              />
            </Grid>
            <Grid item xs={12} md={5} lg={4} sx={{ display: 'flex', alignItems: 'stretch' }}>
              <EmployeeDashboardAttendanceCalendar
                days={calendarDays}
                isLoading={sectionLoading}
                rangeStart={dateRange.startDate}
                rangeEnd={dateRange.endDate}
              />
            </Grid>
            <Grid item xs={12} md={7} lg={8} sx={{ display: 'flex', alignItems: 'stretch' }}>
              <EmployeeDashboardPayrollMonthlyChart
                monthlyPayments={payrollMonthlyPayments}
                isLoading={sectionLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <EmployeeDashboardCharts
                attendanceStats={attendanceStats}
                leaveStats={leaveStats}
                isLoading={sectionLoading}
              />
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
}
